/**
 * LockstepEngine - 帧同步核心引擎
 */

import type { PlayerAction } from './types';

export interface FrameInputs {
  frame: number;
  inputs: Map<0 | 1, PlayerAction[]>;
}

export class LockstepEngine {
  private currentFrame = -1;
  private nextInputFrame = 0;
  private readonly inputBuffer = new Map<number, Map<0 | 1, PlayerAction[]>>();
  private readonly localPlayerId: 0 | 1;
  private readonly inputDelay: number;
  private started = false;

  constructor(localPlayerId: 0 | 1, inputDelay = 2) {
    this.localPlayerId = localPlayerId;
    this.inputDelay = inputDelay;
  }

  start(initialFrame = 0): void {
    this.currentFrame = initialFrame - 1;
    this.nextInputFrame = initialFrame + this.inputDelay;
    this.inputBuffer.clear();
    this.started = true;
  }

  stop(): void {
    this.started = false;
  }

  get frame(): number {
    return this.currentFrame;
  }

  /** 记录本地输入，自动加上 inputDelay */
  recordLocalInput(actions: PlayerAction[]): { frame: number; actions: PlayerAction[] } {
    const targetFrame = this.nextInputFrame++;
    this.setInput(targetFrame, this.localPlayerId, actions);
    return { frame: targetFrame, actions };
  }

  /** 收到远程输入 */
  receiveRemoteInput(frame: number, playerId: 0 | 1, actions: PlayerAction[]): void {
    this.setInput(frame, playerId, actions);
  }

  /** 检查下一帧是否可以推进（两边输入都已到） */
  canAdvance(): boolean {
    if (!this.started) return false;
    const nextFrame = this.currentFrame + 1;
    const frameInputs = this.inputBuffer.get(nextFrame);
    if (!frameInputs) return false;
    return frameInputs.has(0) && frameInputs.has(1);
  }

  /**
   * H5 双浏览器测试时，非焦点页面可能不会稳定发送空输入帧。
   * 对本作这种离散推箱子动作，宁可把缺失的对端输入当作 none，
   * 也不要让本地玩家因为等待空帧而卡住。
   */
  assumeMissingRemoteInputForNextFrame(): void {
    if (!this.started) return;
    const nextFrame = this.currentFrame + 1;
    const frameInputs = this.inputBuffer.get(nextFrame);
    if (!frameInputs?.has(this.localPlayerId)) return;
    if (!frameInputs.has(0)) frameInputs.set(0, [{ type: 'none' }]);
    if (!frameInputs.has(1)) frameInputs.set(1, [{ type: 'none' }]);
  }

  /** 推进一帧，返回该帧的所有玩家输入 */
  advance(): FrameInputs | null {
    if (!this.canAdvance()) return null;
    this.currentFrame++;
    const frameInputs = this.inputBuffer.get(this.currentFrame)!;
    const result: FrameInputs = {
      frame: this.currentFrame,
      inputs: new Map(frameInputs),
    };
    // 清理已执行的帧以释放内存
    this.inputBuffer.delete(this.currentFrame);
    return result;
  }

  /** 预填充前几帧的空输入，用于游戏开始前的缓冲 */
  prefetchEmptyFrames(count: number): void {
    for (let i = 0; i < count; i++) {
      const f = this.currentFrame + 1 + i;
      if (!this.inputBuffer.has(f)) {
        this.inputBuffer.set(f, new Map());
      }
      this.inputBuffer.get(f)!.set(0, [{ type: 'none' }]);
      this.inputBuffer.get(f)!.set(1, [{ type: 'none' }]);
    }
  }

  private setInput(frame: number, playerId: 0 | 1, actions: PlayerAction[]): void {
    if (frame <= this.currentFrame) return;
    if (!this.inputBuffer.has(frame)) {
      this.inputBuffer.set(frame, new Map());
    }
    this.inputBuffer.get(frame)!.set(playerId, actions);
  }
}
